/**
 * Zentrale Faktenquelle der DMD Studio GmbH.
 *
 * Alles, was auf mehreren Seiten, im Footer, in Rechtstexten oder in
 * strukturierten Daten (JSON-LD) vorkommt, steht genau hier – und nur hier.
 * Werte, die noch nicht belegt sind, bleiben `null` und werden in der
 * Übergabe genannt – keine Platzhalter im Code oder auf der Seite. Keine
 * Zahl darf im Code doppelt stehen (siehe CLAUDE.md → Inhalte).
 */
export const COMPANY = {
  legalName: "DMD Studio GmbH",
  name: "DMD Studio",
  tagline: "Design, Marketing & Development aus einer Hand",
  street: "Am Kebigsgraben 21",
  zip: "97616",
  city: "Salz",
  /** Nächstgelegene bekannte Stadt – für Ortsangaben wie „Salz bei Bad Neustadt“. */
  nearCity: "Bad Neustadt a. d. Saale",
  region: "Bayern",
  country: "DE",
  phoneDisplay: "09771 9114945",
  phoneInternational: "+49 9771 9114945",
  phoneTel: "+4997719114945",
  email: "kontakt@dmd-studio.de",
  /** Standort-Koordinaten (identisch mit dem Kartenpin in GoogleMap.tsx). */
  geo: { latitude: 50.3006108, longitude: 10.1934825 },
  mapsUrl: "https://maps.app.goo.gl/di2bUNW2RqKYnd1z9",
  /** Google-Unternehmensprofil (Freigabelink des Inhabers, 2026-10-21). */
  googleBusinessProfileUrl: "https://share.google/HDz8twvNbLmvzRpyu" as string | null,
  social: {
    instagram: "https://www.instagram.com/dmd_studio_gmbh/",
    linkedin: "https://www.linkedin.com/company/dmd-studio-gmbh",
    tiktok: "https://www.tiktok.com/@dmd_studio_gmbh",
    facebook: "https://www.facebook.com/people/DMD-Studio-GmbH/61579523897431/",
  },
  founders: ["Tobias Fischer", "Pascal Reininger", "Sebastian Erhard"],
  register: { court: "Amtsgericht Schweinfurt", number: "HRB 10006" },
} as const;

/** Adresse einzeilig, z. B. für Footer und Kontaktkarten. */
export const ADDRESS_LINE = `${COMPANY.street}, ${COMPANY.zip} ${COMPANY.city}`;
/** Ortsangabe in Fließtext. */
export const LOCATION_PHRASE = `${COMPANY.city} bei ${COMPANY.nearCity}`;

/**
 * Öffnungszeiten – eine Quelle für Footer, Kontaktseite und JSON-LD.
 * (Buchbare Zeiten des Terminkalenders werden separat im Admin gepflegt.)
 */
export const OPENING_HOURS = {
  display: [
    "Mo – Do: 08:30 – 12:00 · 13:00 – 17:00 Uhr",
    "Fr: 08:30 – 12:00 · 13:00 – 16:00 Uhr",
  ],
  closedNote: "Sa, So & Feiertage: geschlossen",
  /** Kurzform für Fließtext, z. B. neben der Telefonnummer. */
  short: "Mo–Do 8:30–17 Uhr, Fr bis 16 Uhr",
  /** schema.org OpeningHoursSpecification */
  specification: [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:30", closes: "12:00" },
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday"], opens: "13:00", closes: "17:00" },
    { days: ["Friday"], opens: "13:00", closes: "16:00" },
  ],
} as const;

/** Regionen, in denen DMD Studio tätig ist (JSON-LD areaServed, Stadt-Seiten). */
export const SERVICE_AREAS = [
  { type: "City", name: "Bad Neustadt an der Saale" },
  { type: "AdministrativeArea", name: "Rhön-Grabfeld" },
  { type: "City", name: "Schweinfurt" },
  { type: "City", name: "Würzburg" },
  { type: "AdministrativeArea", name: "Unterfranken" },
] as const;

/**
 * Zahlen und Zeiträume, die auf der Website genannt werden.
 * `null` = noch nicht belegt → auf der Seite weglassen, in der Übergabe nennen.
 */
export const FACTS = {
  /** Abgeschlossene Projekte – Angabe „50+“ stammt von der bisherigen Startseite; Bestätigung durch das Team steht aus. */
  projectsCompleted: 50,
  /** Dauer eines typischen Website-Projekts (Entscheidung 2026-10-21: 2–6 Wochen; Shops/Web-Apps länger). */
  websiteDurationWeeks: { min: 2, max: 6 },
  /** Kundenstimmen kommen live aus der Datenbank (testimonials, sichtbar). */
  reviewSource: "Kundenstimmen auf dieser Website (Datenbank)",
  /**
   * Dauer eines Erklärvideos von Briefing bis Lieferung (Auftrag 2026-10-28,
   * Paket 2). Annahme 2–3 Wochen laut Auftrag – Bestätigung durch das Team steht aus.
   */
  motionDurationWeeks: { min: 2, max: 3 },
  /**
   * Dauer der ersten nutzbaren Version (MVP) eines Software-Projekts – Angabe
   * „4–8 Wochen“ stammt von der bisherigen Software-Seite; Bestätigung durch das Team steht aus.
   */
  mvpDurationWeeks: { min: 4, max: 8 },
  /** Kurzvideos je halbem Drehtag (Social Media) – Angabe der bisherigen Leistungsseite, übernommen 2026-10-29. */
  socialClipsPerShootDay: { min: 8, max: 12 },
  /**
   * Spanne für eine eigene Software-Lösung im Lizenz-Rechner (Konzept, Umsetzung,
   * Übergabe – ohne laufenden Betrieb). Bestätigung Team ausstehend (Auftrag 2026-10-28, 1.6).
   */
  customSoftwareRangeEur: { from: 12000, to: 35000 },
} as const;

/** „2–6 Wochen“ – eine Schreibweise für alle Seiten. */
export const WEBSITE_DURATION_LABEL = `${FACTS.websiteDurationWeeks.min}–${FACTS.websiteDurationWeeks.max} Wochen`;
/** „4–8 Wochen“ – Dauer bis zur ersten nutzbaren Software-Version. */
export const MVP_DURATION_LABEL = `${FACTS.mvpDurationWeeks.min}–${FACTS.mvpDurationWeeks.max} Wochen`;
/** z. B. „8 bis 12 Clips“ */
export const SOCIAL_CLIPS_LABEL = `${FACTS.socialClipsPerShootDay.min} bis ${FACTS.socialClipsPerShootDay.max} Clips`;
/** „2–3 Wochen“ – Dauer eines Erklärvideos. */
export const MOTION_DURATION_LABEL = `${FACTS.motionDurationWeeks.min}–${FACTS.motionDurationWeeks.max} Wochen`;

/**
 * Preisanker je Leistungsseite – ohne Beträge (Entscheidung 2026-10-21):
 * Festpreis nach Erstgespräch, Zahlungsmodell als Hinweis.
 */
export const PRICE_ANCHORS: Record<string, { headline: string; note: string }> = {
  webdesign: { headline: "Festpreis nach Erstgespräch", note: "Ratenzahlung in drei Schritten möglich: Auftrag, Konzept-Abnahme, Live-Schaltung." },
  seo: { headline: "Festpreis nach Erstgespräch", note: "Monatliche Betreuung oder einmaliges Projekt – Umfang legen wir gemeinsam fest." },
  "social-media-marketing": { headline: "Festpreis nach Erstgespräch", note: "Monatliches Paket nach Umfang; Produktion und Auswertung inklusive." },
  branding: { headline: "Festpreis nach Erstgespräch", note: "Ratenzahlung möglich; Umfang vom Logo bis zum kompletten Erscheinungsbild." },
  "paid-ads": { headline: "Festpreis nach Erstgespräch", note: "Betreuung monatlich, Werbebudget kommt separat dazu." },
  "software-entwicklung": { headline: "Festpreis nach Erstgespräch", note: "Ratenzahlung möglich; Förderung (z. B. Digitalbonus Bayern) prüfen wir mit." },
  wartungsservice: { headline: "Monatlicher Festpreis", note: "Nur für Websites von DMD Studio; Umfang nach Größe der Seite." },
  domains: { headline: "Jährlicher Festpreis je Domain", note: "Registrierung, DNS-Pflege und Ablauf-Überwachung inklusive." },
  "ki-analyse": { headline: "Festpreis nach Vorgespräch", note: "Ein Analysetag vor Ort inklusive Bericht und Maßnahmenplan; Förderfähigkeit klären wir vorab." },
  "motion-design": { headline: "Festpreis pro Video nach Erstgespräch", note: "Skript, Sprecher, Animation, zwei Korrekturrunden und alle Formate inklusive; weitere Sprachversionen günstiger." },
};
